import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { opportunitiesAPI, customersAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { RefreshCw, Plus, TrendingUp, Euro, Target, AlertCircle } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const STAGES = [
  { key: 'new',         label: 'New',          color: '#3b82f6' },
  { key: 'qualified',   label: 'Qualified',    color: '#8b5cf6' },
  { key: 'quoted',      label: 'Quoted',       color: '#f59e0b' },
  { key: 'negotiating', label: 'Negotiating',  color: '#f97316' },
  { key: 'won',         label: 'Won',          color: '#10b981' },
  { key: 'lost',        label: 'Lost',         color: '#ef4444' },
];

const AdminPipeline = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [opportunities, setOpportunities] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null); // { id, fromStage }
  const [dragOver, setDragOver] = useState(null); // stage key

  // New opportunity modal
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ title: '', customerEmail: '', stage: 'new', value: '', probability: 10, expectedCloseAt: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oppsRes, forecastRes] = await Promise.all([
        opportunitiesAPI.getAll({ limit: 200 }),
        opportunitiesAPI.getForecast(),
      ]);
      setOpportunities(oppsRes.data.opportunities || []);
      setForecast(forecastRes.data);
    } catch {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const byStage = (stage) => opportunities.filter(o => o.stage === stage);
  const stageTotal = (stage) => byStage(stage).reduce((s, o) => s + (o.value || 0), 0);

  const handleDragStart = (e, opp) => {
    setDragging({ id: opp._id, fromStage: opp.stage });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    if (!dragging || dragging.fromStage === toStage) { setDragging(null); setDragOver(null); return; }
    try {
      await opportunitiesAPI.update(dragging.id, { stage: toStage });
      setOpportunities(prev => prev.map(o => o._id === dragging.id ? { ...o, stage: toStage } : o));
      toast.success(`Moved to ${STAGES.find(s => s.key === toStage)?.label}`);
    } catch {
      toast.error('Failed to update stage');
    } finally {
      setDragging(null);
      setDragOver(null);
    }
  };

  const handleCreateOpportunity = async () => {
    if (!newForm.title) return;
    try {
      let customerId;
      if (newForm.customerEmail) {
        const res = await customersAPI.getAll({ search: newForm.customerEmail, limit: 1 });
        customerId = res.data.customers?.[0]?._id;
      }
      await opportunitiesAPI.create({
        title: newForm.title,
        stage: newForm.stage,
        value: Number(newForm.value) || 0,
        probability: Number(newForm.probability) || 10,
        expectedCloseAt: newForm.expectedCloseAt || undefined,
        customerId,
      });
      toast.success('Opportunity created');
      setShowNew(false);
      setNewForm({ title: '', customerEmail: '', stage: 'new', value: '', probability: 10, expectedCloseAt: '' });
      load();
    } catch {
      toast.error('Failed to create opportunity');
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Target size={28} color="#f97316" /> Pipeline
            </h1>
            <p style={{ color: '#a1a1aa' }}>Drag opportunities between stages to update their status</p>
          </div>
          <div className="admin-header-right">
            <button onClick={() => setShowNew(true)} style={{ background: '#f97316', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
              <Plus size={15}/> New Opportunity
            </button>
            <button className="btn-secondary" onClick={load}>
              <RefreshCw size={16}/>
            </button>
          </div>
        </div>

        {/* Forecast strip */}
        {forecast && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
            {[
              { label: 'Pipeline Value', value: `€${(forecast.pipelineValue || 0).toFixed(0)}`, color: '#3b82f6', icon: <Euro size={16}/> },
              { label: 'Weighted Value', value: `€${(forecast.weightedValue || 0).toFixed(0)}`, color: '#8b5cf6', icon: <TrendingUp size={16}/> },
              { label: 'Closing This Month', value: `€${(forecast.expectedThisMonth || 0).toFixed(0)}`, color: '#10b981', icon: <Target size={16}/> },
            ].map(f => (
              <div key={f.label} style={{ flex: '1 1 160px', background: '#111', borderRadius: 12, border: `1px solid ${f.color}40`, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ color: f.color }}>{f.icon}</div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: f.color }}>{f.value}</div>
                  <div style={{ fontSize: 12, color: '#71717a' }}>{f.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Opportunity modal */}
        {showNew && (
          <div style={{ background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', color: '#fff' }}>New Opportunity</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { key: 'title', label: 'Title *', type: 'text', full: true },
                { key: 'customerEmail', label: 'Customer Email', type: 'email' },
                { key: 'stage', label: 'Stage', type: 'select', options: STAGES.map(s => s.key) },
                { key: 'value', label: 'Value (€)', type: 'number' },
                { key: 'probability', label: 'Probability (%)', type: 'number' },
                { key: 'expectedCloseAt', label: 'Expected Close', type: 'date' },
              ].map(f => (
                <label key={f.key} style={{ gridColumn: f.full ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                  <span style={{ color: '#71717a' }}>{f.label}</span>
                  {f.type === 'select' ? (
                    <select value={newForm[f.key]} onChange={e => setNewForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }}>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={newForm[f.key]} onChange={e => setNewForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }} />
                  )}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={handleCreateOpportunity} style={{ background: '#f97316', border: 'none', borderRadius: 6, padding: '8px 20px', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Create</button>
              <button onClick={() => setShowNew(false)} style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#71717a' }}>
            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          /* Kanban board */
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16, alignItems: 'flex-start' }}>
            {STAGES.map(stage => (
              <div
                key={stage.key}
                onDragOver={e => { e.preventDefault(); setDragOver(stage.key); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={e => handleDrop(e, stage.key)}
                style={{
                  minWidth: 220, flex: '0 0 220px',
                  background: dragOver === stage.key ? `${stage.color}10` : '#111',
                  borderRadius: 12,
                  border: dragOver === stage.key ? `2px solid ${stage.color}` : '1px solid #27272a',
                  padding: 12,
                  transition: 'border 0.15s, background 0.15s',
                }}
              >
                {/* Column header */}
                <div style={{ marginBottom: 12, paddingBottom: 10, borderBottom: `2px solid ${stage.color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: stage.color, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {stage.label}
                    </span>
                    <span style={{ background: `${stage.color}20`, color: stage.color, borderRadius: 10, padding: '1px 8px', fontSize: 12, fontWeight: 600 }}>
                      {byStage(stage.key).length}
                    </span>
                  </div>
                  <div style={{ color: '#71717a', fontSize: 12, marginTop: 4 }}>
                    €{stageTotal(stage.key).toFixed(0)}
                  </div>
                </div>

                {/* Cards */}
                {byStage(stage.key).map(opp => (
                  <div
                    key={opp._id}
                    draggable
                    onDragStart={e => handleDragStart(e, opp)}
                    onDragEnd={() => setDragging(null)}
                    onClick={() => opp.customerId && navigate(`/admin/customers/${opp.customerId}`)}
                    style={{
                      background: '#18181b',
                      borderRadius: 8,
                      border: `1px solid ${dragging?.id === opp._id ? stage.color : '#27272a'}`,
                      padding: 12,
                      marginBottom: 8,
                      cursor: 'grab',
                      opacity: dragging?.id === opp._id ? 0.5 : 1,
                      transition: 'border 0.15s, opacity 0.15s',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 13, marginBottom: 4 }}>{opp.title}</div>
                    {opp.customerId?.name || opp.customerName ? (
                      <div style={{ color: '#71717a', fontSize: 11, marginBottom: 4 }}>{opp.customerId?.name || opp.customerName}</div>
                    ) : null}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                      <span style={{ color: '#10b981', fontWeight: 700, fontSize: 13 }}>€{(opp.value || 0).toFixed(0)}</span>
                      <span style={{ color: '#f59e0b', fontSize: 11 }}>{opp.probability}%</span>
                    </div>
                    {opp.expectedCloseAt && (
                      <div style={{ color: new Date(opp.expectedCloseAt) < new Date() ? '#ef4444' : '#52525b', fontSize: 11, marginTop: 4 }}>
                        Close: {new Date(opp.expectedCloseAt).toLocaleDateString('it-IT')}
                      </div>
                    )}
                  </div>
                ))}

                {byStage(stage.key).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: '#3f3f46', fontSize: 12 }}>
                    Drop here
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPipeline;
